// UndoConflictHighlightOverlay — conflict-aware Undo の拒否 UI、bbox ハイライト
// (docs/_wip/undo-conflict-aware-2026-07-12.md「B' 実装での追加事項」の TODO を消化)。
// Classic/Modern 共通部品 (2026-07-23 是正: modern-layout.md §1.1 表現パリティ、
// 当初 Modern 限定だった穴を是正)。
//
// ## なぜこの座標変換方式か
// `NameLabelOverlay` / `BrushCursorOverlay` は canvas と**兄弟要素**として
// `CanvasStage` の innerWrapper の中に置かれ wrapper-local px で配置するが、
// `useConflictAwareUndo` (= この overlay が読む state の持ち主) は `CanvasStage` の外・
// 各レイアウト (`ModernLayout` / `ClassicLayout`) 側にそれぞれ配線されている
// (`CanvasStage` は Classic/Modern 共有のレイアウト非依存コンポーネントなので、そこに
// Undo 専用 state を持ち込まない)。そこで **`ImagepasteOverlay` / `ImagepasteToastOverlay`
// と同じ流儀**を踏襲する: `position: fixed` で viewport (client) 座標へ直書きし、
// canvas 座標 → client 座標の変換は `CanvasViewMapper` (`viewport.ts` §1b、zoom / pan /
// flip / 将来の回転を 1 か所に集約) に任せる。この方式は `CanvasStage` のツリーに乗る
// 必要が無いため、Undo ボタン/トーストと同じ各レイアウトの 1 箇所からそのまま配線できる。
// (旧: canvas 要素の `getBoundingClientRect()` の比率計算 + flip 再マップ。回転すると
// rect が外接矩形に膨らんで比率が成り立たなくなるため mapper へ移した。)
//
// ## 絶対条件: キャンバス本体のピクセルには一切書き込まない
// この overlay は DOM (`position: fixed` の `<div>` 群) のみを描画する。
// `CanvasDrawingEngine` / `EngineCore` の layer バッファには一切触れない
// (Undo の巻き戻し対象 = 実ピクセルを汚さない)。
//
// ## 操作を妨げない
// 全要素 `pointer-events: none`。フォーカス/タブ移動にも入らない (aria-hidden)。

import type { Rect } from '@oekaki/drawing-core';
import { useEffect, useState } from 'react';

import type { CanvasViewMapper } from '../../../infrastructure/drawing/viewport.js';
import type { UndoConflictHighlightBox } from '../../hooks/useConflictAwareUndo.js';

export interface UndoConflictHighlightOverlayProps {
  /** 画面 ⇔ canvas 座標の変換窓口 (= ImagepasteOverlay と同じ)。 */
  readonly viewMapper: CanvasViewMapper;
  /** `useConflictAwareUndo().highlightBoxes`。空配列で非表示。 */
  readonly boxes: ReadonlyArray<UndoConflictHighlightBox>;
}

interface ViewportRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * canvas 座標の inclusive bbox (`{x0,y0,x1,y1}`) → viewport-px 矩形への変換。
 * inclusive なので幅高さは `x1 - x0 + 1` / `y1 - y0 + 1`。変換 (zoom / pan / flip /
 * 回転) は mapper に委譲し、回転時は外接矩形を返す (テスト容易性のため component から分離)。
 */
export const computeHighlightViewportRect = (input: {
  readonly viewMapper: Pick<CanvasViewMapper, 'canvasRectToClientBounds'>;
  readonly bbox: Rect;
}): ViewportRect => {
  const { viewMapper, bbox } = input;
  return viewMapper.canvasRectToClientBounds(
    bbox.x0,
    bbox.y0,
    bbox.x1 - bbox.x0 + 1,
    bbox.y1 - bbox.y0 + 1,
  );
};

/**
 * canvas rect / window の変化 (resize / scroll、スクロールコンテナ内も window の
 * capture:true listener で拾える — `ImagepasteOverlay.useOverlayAnchor` と同じ理由)
 * に追従して viewport-px を再計算するフック。
 */
const useHighlightViewportRects = (
  viewMapper: CanvasViewMapper,
  boxes: ReadonlyArray<UndoConflictHighlightBox>,
): ReadonlyArray<{
  readonly id: number;
  readonly fading: boolean;
  readonly rect: ViewportRect;
}> => {
  const [rects, setRects] = useState<
    ReadonlyArray<{ readonly id: number; readonly fading: boolean; readonly rect: ViewportRect }>
  >([]);

  useEffect(() => {
    if (boxes.length === 0) {
      setRects([]);
      return;
    }
    if (typeof window === 'undefined') return;

    const recompute = (): void => {
      setRects(
        boxes.map((b) => ({
          id: b.id,
          fading: b.fading,
          rect: computeHighlightViewportRect({ viewMapper, bbox: b.bbox }),
        })),
      );
    };
    recompute();

    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, { passive: true, capture: true });

    // 2026-09-29 viewport transform 移行 (決定5、fable-advisor 指摘): 旧 scroll
    // 方式は pan/zoom-anchor 補正が `wrapper.scrollLeft/Top` を書き換えるたびに
    // scroll イベントが window の capture リスナーへバブルし recompute を間接的に
    // 駆動していたが、新方式は `transform` を直接書くだけで DOM イベントが発火
    // しないため、ハイライト表示中 (= フェードアウトまでの数秒) に pan/zoom すると
    // 赤枠が実際の conflict 領域からズレたまま固定される (= 荒らし復旧 UI の
    // 信頼性に関わるので特に重要)。軽量な rAF ポーリングで補う
    // (`ImagepasteOverlay.useOverlayAnchor` と同流儀、変化が無いフレームは
    // setState しない)。
    let raf = 0;
    let lastRects: ReadonlyArray<{
      readonly id: number;
      readonly fading: boolean;
      readonly rect: ViewportRect;
    }> | null = null;
    const rectsEqual = (
      a: ReadonlyArray<{
        readonly id: number;
        readonly fading: boolean;
        readonly rect: ViewportRect;
      }>,
      b: typeof a,
    ): boolean =>
      a.length === b.length &&
      a.every((x, i) => {
        const y = b[i];
        return (
          y !== undefined &&
          x.id === y.id &&
          x.fading === y.fading &&
          x.rect.left === y.rect.left &&
          x.rect.top === y.rect.top &&
          x.rect.width === y.rect.width &&
          x.rect.height === y.rect.height
        );
      });
    const tick = (): void => {
      raf = window.requestAnimationFrame(tick);
      const next = boxes.map((b) => ({
        id: b.id,
        fading: b.fading,
        rect: computeHighlightViewportRect({ viewMapper, bbox: b.bbox }),
      }));
      if (lastRects !== null && rectsEqual(lastRects, next)) return;
      lastRects = next;
      setRects(next);
    };
    raf = window.requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, { capture: true });
      window.cancelAnimationFrame(raf);
    };
    // boxes を丸ごと依存に含める: fading の切替 (= 同じ id・違う中身) でも再計算が
    // 走ってよい (recompute は軽量、canvas 未変化なら同じ結果を返すだけ)。
  }, [viewMapper, boxes]);

  return rects;
};

const SHAKE_CSS = `
@keyframes oekaki-undo-conflict-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
}
.oekaki-undo-conflict-shake { animation: oekaki-undo-conflict-shake 400ms ease-in-out 1; }
@media (prefers-reduced-motion: reduce) { .oekaki-undo-conflict-shake { animation: none; } }
`;

export const UndoConflictHighlightOverlay = ({
  viewMapper,
  boxes,
}: UndoConflictHighlightOverlayProps): React.ReactNode => {
  const rects = useHighlightViewportRects(viewMapper, boxes);

  if (rects.length === 0) return null;

  return (
    <div
      aria-hidden="true"
      data-oekaki-undo-conflict-highlight-overlay
      // 個々の矩形は position:fixed (viewport 基準) なので、このラッパ自体には
      // 位置の意味は無い — グルーピング用の no-op コンテナ。
      style={{ pointerEvents: 'none' }}
    >
      {/* 揺れは出た瞬間の 1 回だけ (key が世代 id なので、連打の延長では再生し直さない)。
          prefers-reduced-motion では止める。 */}
      <style>{SHAKE_CSS}</style>
      {rects.map(({ id, fading, rect }, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: 同じ拒否 (id) の矩形が複数あり、並びは世代内で固定
          key={`${id}-${i}`}
          data-oekaki-undo-conflict-highlight-box
          className="oekaki-undo-conflict-shake"
          style={{
            position: 'fixed',
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
            zIndex: 65,
            pointerEvents: 'none',
            boxSizing: 'border-box',
            // 「上に描かれていて戻せない」と文字なしで見えるように、太めの点線枠 + 薄い赤の
            // 塗り + 左上の鍵バッジ。二重色取り (黒の縁) は背景の明暗どちらでも視認できる流儀。
            border: '3px dashed rgba(255, 70, 70, 0.95)',
            background: 'rgba(255, 70, 70, 0.14)',
            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.55), 0 0 8px 2px rgba(255, 70, 70, 0.6)',
            borderRadius: 3,
            opacity: fading ? 0 : 1,
            transition: 'opacity 400ms ease-out',
          }}
        >
          <span
            data-oekaki-undo-conflict-highlight-badge
            style={{
              position: 'absolute',
              left: -3,
              top: -3,
              transform: 'translate(-35%, -55%)',
              width: 22,
              height: 22,
              borderRadius: 11,
              background: 'rgb(220, 40, 40)',
              border: '2px solid #fff',
              boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* 鍵 (= 上の線に押さえられていて動かせない) */}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
              <path d="M17 9V7a5 5 0 0 0-10 0v2H5v12h14V9h-2zM9 7a3 3 0 0 1 6 0v2H9V7z" />
            </svg>
          </span>
        </div>
      ))}
    </div>
  );
};
